import { httpApi } from '@api/http.api.ts';
import type { ActionType } from '@components/table/types.ts';
import { NO_REQUEST_TIMEOUT } from '@constants/api.ts';
import type { CommonTableRequest } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    MatrixPathwayCategory,
    MatrixPathwayStatus,
    MatrixPathwayTimeUnit,
    MatrixPathwayTrigger,
    MatrixPathwayType,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type CommonMatricesRequest = CommonTableRequest;

export interface RequirementHistory {
    id: number;
    timestampUTC: string;
    action: ActionType;
    reqID: string;
    pathwayName: string;
    scopeSummary: string;
    performedBy: string;
}

export interface AdaptiveTestReqAndRes {
    enabled: boolean;
    finalQuizExempted: boolean;
    availableAfterRefresher: boolean;
}

// matrices
export interface MatrixPathwayPeriod {
    value: number;
    unit: MatrixPathwayTimeUnit;
}

export interface MatrixApplicableRank {
    id: number;
    name: string;
    shortCode: string;
    tenantShortCode: string;
    tenantName: string;
    vesselDepartmentId: number | null;
    vesselDepartmentName: string | null;
    proficiencyLevelId: number | null;
    proficiencyLevelName: string | null;
    promotionRankId: number | null;
    promotionRankName: string | null;
    supervisingRankId: number | null;
    supervisingRankName: string | null;
    isActive: boolean;
    createdAtUTC: string | null;
    itemOrder: number;
}

export interface MatrixVesselGroupResponseItem {
    id: number;
    name: string;
    shortCode: string;
    isBuiltIn: boolean;
    isActive: boolean;
    createdUTC: string | null;
    updatedUTC: string | null;
    createdAtUTC: string | null;
}

export interface MatrixVesselTypeResponseItem {
    id: number;
    type: string;
    shortCode: string;
}

export interface MatrixVesselSubTypeResponseItem {
    id: number;
    vesselTypeId: number;
    type: string;
    shortCode: string;
    vesselType: MatrixVesselTypeResponseItem;
    isBuiltIn: boolean;
    isActive: boolean;
    createdUTC: string | null;
    updatedUTC: string | null;
    createdAtUTC: string | null;
}

export interface MatrixVesselResponseItem {
    id: number;
    name: string;
    imoNumber: string;
    vesselTypeId: number;
    vesselSubTypeId: number;
    vesselGroupId: number;
}

export interface MatrixRankScope {
    departmentIds: number[];
    proficiencyLevelIds: number[];
    rankReferenceIds: number[];
    applicableRanks?: MatrixApplicableRank[];
    hasScope?: boolean;
}

export interface MatrixVesselScope {
    vesselGroupIds: number[];
    vesselTypeIds: number[];
    vesselSubTypeIds: number[];
    hasScope?: boolean;
    vesselGroups?: MatrixVesselGroupResponseItem[];
    vesselSubTypes?: MatrixVesselSubTypeResponseItem[];
    vessels?: MatrixVesselResponseItem[] | null;
}

export interface SubscribedPathway {
    hasPracticeQuiz: boolean;
    categoryFocus: string;
    pathwayName: string;
    availableRankScope: MatrixRankScope;
    availableVesselScope: MatrixVesselScope;
    requirements: MatrixRequirement[];
    pathwayId: number;
}

export interface MatrixRequirement {
    id: number;
    lrId: string | null;
    pathwayId: number | null;
    status: MatrixPathwayStatus;
    isActive: boolean;
    requirementType: MatrixPathwayType | null;
    learningCategory: MatrixPathwayCategory | null;
    trigger: MatrixPathwayTrigger | null;
    duePeriod?: MatrixPathwayPeriod | null;
    dueValue: number | null;
    dueFormat: MatrixPathwayTimeUnit | null;
    dueNoticePeriod?: MatrixPathwayPeriod | null;
    dueNoticeValue: number | null;
    dueNoticeFormat: MatrixPathwayTimeUnit | null;
    refresh: boolean;
    refreshPeriod?: MatrixPathwayPeriod | null;
    refreshPeriodValue: number | null;
    refreshPeriodFormat: MatrixPathwayTimeUnit | null;
    rankScope: MatrixRankScope | null;
    vesselScope: MatrixVesselScope | null;
    adaptivePreTest: boolean;
    createdAtUtc: string | null;
    updatedAtUtc: string | null;
    firstActivationAtUTC?: string | null; // if user set isActive to true at least once
    canEnableAdaptivePreTest: boolean; // used for enabling toggle for adaptivePreTest, false in backend if pathway has no practice quiz
}

export interface MatrixUpsertRequest {
    requirementType: MatrixPathwayType | null;
    learningCategory: MatrixPathwayCategory | null;
    trigger: MatrixPathwayTrigger | null;
    dueValue: number | null;
    dueFormat: MatrixPathwayTimeUnit | null;
    dueNoticeValue: number | null;
    dueNoticeFormat: MatrixPathwayTimeUnit | null;
    refresh: boolean;
    refreshPeriodValue: number | null;
    refreshPeriodFormat: MatrixPathwayTimeUnit | null;
    rankScope: MatrixRankScope | null;
    vesselScope: MatrixVesselScope | null;
    adaptivePreTest: boolean;
    isActive: boolean;
}

export interface MandatoryGateSettings {
    isLibraryLocked: boolean;
    isRecommendedLocked: boolean;
    isCareerRoadmapLocked: boolean;
}

export interface MatricesSettingsResponse {
    tenantAdaptiveSettingsDto: AdaptiveTestReqAndRes;
    tenantMandatoryGateSettings: MandatoryGateSettings;
}

const getTenantBaseUrl = (clientId: number, tenantAlias: string) =>
    `admin-center/clients/${clientId}/tenants/${tenantAlias}`;

export const getApiTenantRequirementHistory = (
    clientId: number,
    tenantAlias: string,
    payload: CommonMatricesRequest,
): Promise<PaginatedResponse<RequirementHistory, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<RequirementHistory, EmptyResultsDescription>
        >(`${getTenantBaseUrl(clientId, tenantAlias)}/requirement-history`, payload)
        .then(({ data }) => data);

export const getApiMatrixRequirement = (
    clientId: number,
    tenantAlias: string,
    payload: CommonMatricesRequest,
): Promise<PaginatedResponse<SubscribedPathway, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<SubscribedPathway, EmptyResultsDescription>
        >(`${getTenantBaseUrl(clientId, tenantAlias)}/matrix-requirement/search`, payload)
        .then(({ data }) => data);

export const createApiMatrixRequirement = (
    clientId: number,
    tenantAlias: string,
    pathwayId: number,
    payload: MatrixUpsertRequest,
): Promise<PaginatedResponse<SubscribedPathway, EmptyResultsDescription>> =>
    httpApi
        .post<PaginatedResponse<SubscribedPathway, EmptyResultsDescription>>(
            `${getTenantBaseUrl(clientId, tenantAlias)}/matrix-requirement/${pathwayId}`,
            payload,
            {
                timeout: NO_REQUEST_TIMEOUT,
            },
        )
        .then(({ data }) => data);

export const updateApiMatrixRequirement = (
    clientId: number,
    tenantAlias: string,
    id: number,
    payload: MatrixUpsertRequest,
): Promise<PaginatedResponse<SubscribedPathway, EmptyResultsDescription>> =>
    httpApi
        .put<PaginatedResponse<SubscribedPathway, EmptyResultsDescription>>(
            `${getTenantBaseUrl(clientId, tenantAlias)}/matrix-requirement/${id}`,
            payload,
            {
                timeout: NO_REQUEST_TIMEOUT,
            },
        )
        .then(({ data }) => data);

// this is not delete matrix requirement item, it is reverting to default record
export const deleteApiMatrixRequirement = (clientId: number, tenantAlias: string, id: number): Promise<any> =>
    httpApi
        .delete(`${getTenantBaseUrl(clientId, tenantAlias)}/matrix-requirement/${id}`, {
            timeout: NO_REQUEST_TIMEOUT,
        })
        .then(({ data }) => data);

export const getApiMatricesSettings = (clientId: number, tenantAlias: string): Promise<MatricesSettingsResponse> =>
    httpApi
        .get<MatricesSettingsResponse>(`${getTenantBaseUrl(clientId, tenantAlias)}/matrices-settings`)
        .then(({ data }) => data);

export const putApiMatricesSettingsMandatoryGate = (
    clientId: number,
    tenantAlias: string,
    payload: MandatoryGateSettings,
): Promise<void> =>
    httpApi
        .put(`${getTenantBaseUrl(clientId, tenantAlias)}/matrices-settings/mandatory-gate-settings`, payload)
        .then(({ data }) => data);

export const putApiMatricesSettingsAdaptive = (
    clientId: number,
    tenantAlias: string,
    payload: AdaptiveTestReqAndRes,
): Promise<AdaptiveTestReqAndRes> =>
    httpApi
        .put<AdaptiveTestReqAndRes>(`${getTenantBaseUrl(clientId, tenantAlias)}/matrices-settings/adaptive`, payload)
        .then(({ data }) => data);
